import { BuilderContext, targetFromTargetString } from '@angular-devkit/architect';
import { logging } from '@angular-devkit/core';
import path from 'path';

import { BuildTarget, AngularOutputPath, isOutputPathObject } from '../interfaces';
import { Schema } from './schema';

export default async function deploy(
  engine: {
    run: (
      dir: string,
      options: Schema,
      logger: logging.LoggerApi
    ) => Promise<void>;
  },
  context: BuilderContext,
  buildTarget: BuildTarget,
  options: Schema
) {
  // 1. BUILD
  if (options.noBuild) {
    context.logger.info(`📦 Skipping build`);
  } else {
    if (!context.target) {
      throw new Error('Cannot execute the build target');
    }

    const overrides = {
      ...(options.baseHref && { baseHref: options.baseHref })
    };

    context.logger.info(`📦 Building "${context.target.project}"`);
    context.logger.info(`📦 Build target "${buildTarget.name}"`);

    const build = await context.scheduleTarget(
      targetFromTargetString(buildTarget.name),
      {
        ...buildTarget.options,
        ...overrides
      }
    );
    const buildResult = await build.result;

    if (!buildResult.success) {
      throw new Error('Error while building the app.');
    }
  }

  // 2. DEPLOYMENT

  let dir: string;
  if (options.dir) {

    dir = options.dir;

  } else {

    const buildOptions = await context.getTargetOptions(
      targetFromTargetString(buildTarget.name)
    );

    // Output path configuration
    // The outputPath option can be either:
    // - undefined (Angular 20+): uses default dist/<project-name>/browser
    // - a String which will be used as the base value + default value 'browser'
    // - or an Object for more fine-tune configuration.
    // see https://angular.io/guide/workspace-config#output-path-configuration
    // see https://github.com/angular/angular-cli/pull/26675

    const outputPath = buildOptions.outputPath as AngularOutputPath | undefined;

    if (outputPath === undefined) {
      // Angular 20+ default: dist/<project-name>/browser
      // Extract project name from buildTarget.name (format: "project:target:configuration")
      const projectName = buildTarget.name.split(':')[0];
      dir = path.join('dist', projectName, 'browser');
    } else if (typeof outputPath === 'string') {
      dir = path.join(outputPath, 'browser');
    } else if (isOutputPathObject(outputPath)) {
      // browser defaults to 'browser' per Angular CLI schema
      // Explicit empty string '' means no subfolder (browser files directly in base)
      dir = path.join(outputPath.base, outputPath.browser ?? 'browser');
    } else {
      throw new Error(
        `Unsupported outputPath configuration in angular.json for '${buildTarget.name}'. ` +
        `Expected string or {base, browser} object.`
      );
    }
  }

  await engine.run(
    dir,
    options,
    context.logger
  );
}
